import React, { ComponentType } from 'react';
import { useSelector } from 'react-redux';
import { getAccessToken } from 'js/reducers/session/selectors';

/**
 * The signed-in user's Nexla session token — the credential sent as `Authorization: Bearer`
 * on Nexla API calls.
 *
 * Aliased deliberately. In the OAuth credential flows an "access token" is just as likely to
 * mean the *provider's* token (`oauth2.access_token`, `authInfo.accessToken`, `vendor.access_token`),
 * and both live in the same files. Naming this one for what it is keeps the two apart at a glance.
 *
 * This aliases rather than renames `getAccessToken`, which has ~30 usages across the middleware
 * and the RTK Query APIs; renaming those is its own change.
 */
export { getAccessToken as getNexlaSessionToken } from 'js/reducers/session/selectors';

export const useNexlaSessionToken = (): string | null => useSelector(getAccessToken);

/**
 * Supplies the Nexla session token as a prop. For class components that are not `connect`ed —
 * mirrors the `withSnackbar` HOC these components are already wrapped in.
 */
export function withNexlaSessionToken<P extends object>(WrappedComponent: ComponentType<P>) {
  const WithNexlaSessionTokenComponent = (props: P) => (
    <WrappedComponent {...props} nexlaSessionToken={useNexlaSessionToken()} />
  );

  WithNexlaSessionTokenComponent.displayName = `withNexlaSessionToken(${
    WrappedComponent.displayName || WrappedComponent.name || 'Component'
  })`;

  return WithNexlaSessionTokenComponent;
}

export default withNexlaSessionToken;
